import { 
  collection, 
  doc, 
  setDoc, 
  onSnapshot, 
  serverTimestamp,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase';

export type PresenceStatus = 'online' | 'away' | 'offline';

export interface AdminPresenceRecord {
  uid: string;
  email: string;
  displayName: string;
  role: string;
  avatarUrl?: string;
  status: PresenceStatus;
  lastSeen?: any;
  updatedAt?: any;
}

/**
 * Updates an admin's online/offline presence in Firestore.
 */
export const setAdminPresence = async (
  uid: string,
  email: string,
  displayName: string,
  role: string,
  status: PresenceStatus,
  avatarUrl?: string
): Promise<void> => {
  if (!uid) return;
  try {
    const presenceRef = doc(db, 'admin_presence', uid);
    await setDoc(
      presenceRef,
      {
        uid,
        email,
        displayName: displayName || email.split('@')[0],
        role: role || 'super_admin',
        avatarUrl: avatarUrl || null,
        status,
        lastSeen: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn('Failed to update admin presence:', err);
  }
};

/**
 * Subscribes to all admin presence records in real time.
 */
export const subscribeToAdminPresence = (
  callback: (presences: AdminPresenceRecord[]) => void
): (() => void) => {
  const presenceCol = collection(db, 'admin_presence');
  const q = query(presenceCol, orderBy('lastSeen', 'desc'));

  return onSnapshot(
    q,
    (snapshot) => {
      const records: AdminPresenceRecord[] = [];
      const now = Date.now();
      const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;

      snapshot.docs.forEach((d) => {
        const data = d.data() as AdminPresenceRecord;
        let lastSeenMs = now;
        if (data.lastSeen?.toMillis) {
          lastSeenMs = data.lastSeen.toMillis();
        } else if (data.lastSeen?.seconds) {
          lastSeenMs = data.lastSeen.seconds * 1000;
        }

        // Auto-downgrade to offline if no heartbeat in 15 minutes
        const isStale = (now - lastSeenMs) > FIFTEEN_MINUTES_MS;
        const computedStatus: PresenceStatus = isStale ? 'offline' : (data.status || 'offline');

        records.push({
          ...data,
          uid: d.id,
          status: computedStatus,
        });
      });

      callback(records);
    },
    (err) => {
      console.warn('Presence subscription error:', err);
      callback([]);
    }
  );
};

/**
 * Sets up presence heartbeat for current session.
 */
export const startPresenceHeartbeat = (
  uid: string,
  email: string,
  displayName: string,
  role: string,
  avatarUrl?: string
): (() => void) => {
  if (!uid) return () => {};

  // 1. Mark online initially
  setAdminPresence(uid, email, displayName, role, 'online', avatarUrl);

  // 2. Heartbeat every 60 seconds
  const intervalId = setInterval(() => {
    setAdminPresence(uid, email, displayName, role, 'online', avatarUrl);
  }, 60000);

  // 3. Tab visibility changes
  const handleVisibilityChange = () => {
    if (document.hidden) {
      setAdminPresence(uid, email, displayName, role, 'away', avatarUrl);
    } else {
      setAdminPresence(uid, email, displayName, role, 'online', avatarUrl);
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // 4. Window unload
  const handleBeforeUnload = () => {
    setAdminPresence(uid, email, displayName, role, 'offline', avatarUrl);
  };
  window.addEventListener('beforeunload', handleBeforeUnload);

  // Return teardown function
  return () => {
    clearInterval(intervalId);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('beforeunload', handleBeforeUnload);
    setAdminPresence(uid, email, displayName, role, 'offline', avatarUrl);
  };
};
